/**
 * ActionCard — Attention-grabbing card for pending actions.
 *
 * Shows what needs the user's attention with priority indicator,
 * description, stats, and a call-to-action link.
 *
 * Props:
 *   title: string — what needs attention
 *   description: string — explanation
 *   priority: 'high' | 'medium' | 'low'
 *   variant: 'error' | 'warning' | 'info' | 'success' — icon background color
 *   icon: ReactNode — icon element
 *   stats: Array<{ value: string|number, label: string }> — metric chips
 *   actionLabel: string — CTA text (default: "Review")
 *   onClick: () => void — action handler
 */

import React from 'react';
import styles from './ActionCard.module.css';

function ActionCard({
  title,
  description,
  priority = 'low',
  variant = 'info',
  icon,
  stats = [],
  actionLabel = 'Review',
  onClick,
}) {
  return (
    <button className={styles.card} onClick={onClick} type="button">
      <div className={`${styles.priority} ${styles[`priority-${priority}`]}`} />

      <div className={styles.header}>
        <div className={`${styles.iconBox} ${styles[`icon-${variant}`]}`}>
          {icon}
        </div>
        <div className={styles.title}>{title}</div>
      </div>

      {description && (
        <div className={styles.description}>{description}</div>
      )}

      <div className={styles.footer}>
        {stats.map((stat, idx) => (
          <div key={idx} className={styles.stat}>
            <strong>{stat.value}</strong> <span>{stat.label}</span>
          </div>
        ))}
        <div className={styles.action}>
          {actionLabel}
          <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
            <path d="M5 12h14"/><path d="m12 5 7 7-7 7"/>
          </svg>
        </div>
      </div>
    </button>
  );
}

export default ActionCard;
